import React from 'react';
import {
	TableContainer,
	Paper,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TableSortLabel,
	useTheme,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import moment from 'moment';

const LabelsTable = (props) => {
	const theme = useTheme();
	const order = props.order || 'desc';
	const orderBy = props.orderBy || 'id';

	// Mapeamento entre headers e campos dos dados
	// IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
	const getFieldName = (headerTitle) => {
		const headerMap = {
			'Data de criação': 'date',
			'Selo': 'name',
			'Cobrança': null, // Não ordenável (campo calculado)
		};

		return headerMap[headerTitle] || null;
	};

	// Handler para mudança de ordenação - comunica ao componente pai
	const handleRequestSort = (property) => {
		if (!props.onSortChange) return;

		const isAsc = orderBy === property && order === 'asc';
		const newOrder = isAsc ? 'desc' : 'asc';
		const newOrderBy = property;

		props.onSortChange(newOrderBy, newOrder);
	};

	// Função helper para validar se um valor é válido.
	// Para percentuais, allowZero=true permite 0.
	const isValidValue = (value, allowZero = false) => {
		if (value === null || value === undefined) {
			return false;
		}

		if (typeof value === 'string') {
			if (value.trim() === '') {
				return false;
			}

			const numericValue = Number(value);

			if (Number.isNaN(numericValue)) {
				return false;
			}

			return allowZero ? numericValue >= 0 : numericValue > 0;
		}

		if (typeof value === 'number') {
			if (Number.isNaN(value)) {
				return false;
			}

			return allowZero ? value >= 0 : value > 0;
		}

		return true;
	};

	const paymentTypeHandler = (label) => {
		/*
		 * Cobranças fixa e por faixa têm prioridade.
		 * Isso mantém a visualização coerente caso existam campos antigos
		 * de percentual salvos junto com uma cobrança fixa/por faixa.
		 */
		if (isValidValue(label.orb_flat_fee)) {
			return `R$ ${label.orb_flat_fee} fixo`;
		}

		if (isValidValue(label.orb_track_fee)) {
			return `R$ ${label.orb_track_fee} por faixa`;
		}

		const hasGeneral = isValidValue(
			label.orb_percent_fee,
			true
		);

		const hasInterpreter = isValidValue(
			label.orb_percent_fee_interpreter,
			true
		);

		const hasPhonographicProducer = isValidValue(
			label.orb_percent_fee_phonographic_producer,
			true
		);

		if (hasGeneral || hasInterpreter || hasPhonographicProducer) {
			const fees = [];

			if (hasGeneral) {
				fees.push(`Geral: ${label.orb_percent_fee}%`);
			}

			if (hasInterpreter) {
				fees.push(
					`I: ${label.orb_percent_fee_interpreter}%`
				);
			}

			if (hasPhonographicProducer) {
				fees.push(
					`PF: ${label.orb_percent_fee_phonographic_producer}%`
				);
			}

			const base =
				label.orb_percent_on_net_value === true
					? 'sob o líquido'
					: 'sob o bruto';

			return `${fees.join(' | ')} ${base}`;
		}

		return 'SEM COBRANÇA CADASTRADA';
	};

	return (
		<>
			<TableContainer
				className='table-container'
				component={Paper}
			>
				<Table
					size='small'
					aria-label='collapsible table'
					className='basic-table'
				>
					<TableHead className='basic-table-head'>
						<TableRow>
							{[
								'Data de criação',
								'Selo',
								'Cobrança',
							].map((title, key) => {
								const fieldName =
									getFieldName(title);

								const isSortable =
									fieldName !== null;

								return (
									<TableCell
										key={key}
										align='center'
										style={{
											color: '#F5F5F5',
										}}
										sortDirection={
											orderBy ===
											fieldName
												? order
												: false
										}
									>
										{isSortable ? (
											<TableSortLabel
												active={
													orderBy ===
													fieldName
												}
												direction={
													orderBy ===
													fieldName
														? order
														: 'asc'
												}
												onClick={() =>
													handleRequestSort(
														fieldName
													)
												}
												sx={{
													'&.Mui-active':
														{
															color:
																theme
																	.palette
																	.secondary
																	.main,
														},
													'& .MuiTableSortLabel-icon':
														{
															color:
																orderBy ===
																fieldName
																	? `${theme.palette.secondary.main} !important`
																	: 'rgba(255, 255, 255, 0.54)',
														},
													'&:hover':
														{
															color:
																theme
																	.palette
																	.secondary
																	.light,
														},
													'&:hover .MuiTableSortLabel-icon':
														{
															color: `${theme.palette.secondary.light} !important`,
														},
													color:
														orderBy ===
														fieldName
															? theme
																	.palette
																	.secondary
																	.main
															: 'inherit',
												}}
											>
												{title}
											</TableSortLabel>
										) : (
											title
										)}
									</TableCell>
								);
							})}
						</TableRow>
					</TableHead>

					<TableBody>
						{props.labels?.length > 0
							? props.labels.map(
									(label, idx) => {
										return (
											<TableRow
												className='row'
												key={
													label.id ??
													idx
												}
											>
												{props.hasRole([
													'LABELS_WRITE',
												]) ? (
													<>
														<TableCell
															className='clickable'
															align='center'
															onClick={() =>
																props.showLabelForm(
																	label
																)
															}
														>
															{moment(
																label.date
															).format(
																'MM/YYYY'
															)}{' '}
														</TableCell>

														<TableCell
															className='clickable'
															align='center'
															onClick={() =>
																props.showLabelForm(
																	label
																)
															}
														>
															{
																label.name
															}{' '}
														</TableCell>

														<TableCell
															className='clickable'
															align='center'
															onClick={() =>
																props.showLabelForm(
																	label
																)
															}
														>
															{paymentTypeHandler(
																label
															)}{' '}
														</TableCell>
													</>
												) : props.hasRole([
														'LABELS_READ',
												  ]) ? (
													<>
														<TableCell
															align='center'
															className=''
														>
															{moment(
																label.date
															).format(
																'MM/YYYY'
															)}{' '}
														</TableCell>

														<TableCell
															align='center'
															className=''
														>
															{
																label.name
															}{' '}
														</TableCell>

														<TableCell
															align='center'
															className=''
														>
															{paymentTypeHandler(
																label
															)}{' '}
														</TableCell>
													</>
												) : null}
											</TableRow>
										);
									}
							  )
							: null}
					</TableBody>
				</Table>
			</TableContainer>

			<Pagination
				shape='rounded'
				className='table-pagination'
				count={props.pages}
				page={props.current_page}
				onChange={(e, value) => {
					props.onPageChange(value);
				}}
			/>
		</>
	);
};

export default LabelsTable;
